import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDbtConnections, useValidateDbtConnection } from '../../hooks/useDbtData';
import DbtConnectionCard from './DbtConnectionCard';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

/**
 * DbtConnectionList Component
 * Lists all dbt connections with filtering and actions
 */
const DbtConnectionList = () => {
  const { data: connections, isLoading, error, refetch } = useDbtConnections();
  const validateConnection = useValidateDbtConnection();
  const [filter, setFilter] = useState('all'); // all, core, cloud

  const handleValidate = async (connectionId) => {
    try {
      await validateConnection.mutateAsync(connectionId);
      // Show success message
      console.log('Connection validated successfully');
    } catch (error) {
      console.error('Validation failed:', error);
      // Show error message
    }
  };

  const handleSync = (connectionId) => {
    // Navigate to sync/process artifacts page
    window.location.href = `/dbt/connections/${connectionId}/sync`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading connections: {error.message}</span>
      </div>
    );
  }

  // Filter connections
  const filteredConnections = connections?.filter(conn => {
    if (filter === 'all') return true;
    return conn.connection_type === filter;
  }) || [];

  if (!connections || connections.length === 0) {
    return (
      <EmptyState
        title="No dbt Connections"
        description="Get started by creating your first dbt connection to sync your dbt projects."
        actionText="Create Connection"
        actionLink="/dbt/connections/new"
        secondaryActionText="Learn More"
        secondaryActionLink="/documentation/dbt"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">dbt Connections</h2>
          <p className="text-sm text-base-content/70 mt-1">
            Manage your dbt Core and Cloud connections
          </p>
        </div>
        <Link to="/dbt/connections/new" className="btn btn-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Add Connection
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
        >
          All ({connections.length})
        </button>
        <button
          onClick={() => setFilter('core')}
          className={`btn btn-sm ${filter === 'core' ? 'btn-primary' : 'btn-ghost'}`}
        >
          dbt Core ({connections.filter(c => c.connection_type === 'core').length})
        </button>
        <button
          onClick={() => setFilter('cloud')}
          className={`btn btn-sm ${filter === 'cloud' ? 'btn-primary' : 'btn-ghost'}`}
        >
          dbt Cloud ({connections.filter(c => c.connection_type === 'cloud').length})
        </button>
        <button
          onClick={() => refetch()}
          className="btn btn-sm btn-ghost ml-auto"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clipRule="evenodd"
            />
          </svg>
          Refresh
        </button>
      </div>

      {/* Connection Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredConnections.map((connection) => (
          <DbtConnectionCard
            key={connection.id}
            connection={connection}
            onValidate={handleValidate}
            onSync={handleSync}
          />
        ))}
      </div>

      {filteredConnections.length === 0 && filter !== 'all' && (
        <div className="text-center py-12">
          <p className="text-base-content/60">
            No {filter === 'core' ? 'dbt Core' : 'dbt Cloud'} connections found.
          </p>
        </div>
      )}
    </div>
  );
};

export default DbtConnectionList;
